import React, { useEffect, useState, useRef } from "react";
import "swiper/css";
import "swiper/css/navigation";
import DashboardSuggestion from "./DashboardSuggestion";
import DashboardPastPractices from "./DashboardPastPractices";
import HeaderImage from "@assets/images/mindFulnessHeader.png";
import MainHeader from "@components/ui/MainHeader";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { fetchMindfulnessData } from "api/mindfulness";
import { toast } from "react-toastify";
import { useLoaderSlides } from "data/uiData/LoadersData";
import ReacreationLoading from "@components/ui/RecreationLoading";
import usePlanTimer from "@utils/DietPlanProgress";
import Education from "@features/sleep/Education";
import stepBack from "@assets/images/stepsBack.jpg";
import { LockSleepCoach } from "@utils/defaultImages.js";
import { mindfulnessSliderData } from "data/uiData/MindfulnessSliderData";
import SleepCoachMilestoneModal from "@features/sleep/SleepCoachMilestoneModal";

const MindfulnessDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState("Weekly Overview");
  const [adviseData, setAdviseData] = useState(null);
  const [filterRecord, setFilterRecord] = useState(null);
  const [fetchHistory, setFetchHistory] = useState(false);
  const [isPlanLoading, setIsPlanLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const mindfulnessSlides = useLoaderSlides("mindfulness");
  const [planInitiateTime, setPlanInitiateTime] = useState(null);
  const [lastMilestone, setLastMilestone] = useState(true);
  const [unlockedBadges, setUnlockedBadges] = useState([]);
  const [openAchivementModal, setOpenAchivementModal] = useState(false);

  const { timeLeft, setTimeLeft, stopCountdown } = usePlanTimer(
    planInitiateTime,
    120,
  );
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!location?.state) {
      navigate(-1);
    }
  }, [location, navigate]);

  const fetchData = async (loadHIstory = false) => {
    try {
      const {
        data,
        loading,
        start_time,
        last_mindfulness_milestone_achieved,
        mindfulness_milestone_achieved,
      } = await fetchMindfulnessData();
      if (!data && !loading) {
        toast.error(t("Something went wrong. Redirecting you to try again..."));
        navigate("/mindfulness");
        return;
      }

      if (isMountedRef.current) {
        const loadingStatus = loading;

        setIsPlanLoading(loadingStatus);
        setAdviseData(data?.daily_practice);
        setPlanInitiateTime(start_time);
        setFirstLoad(false);
        setLastMilestone(last_mindfulness_milestone_achieved);
        setUnlockedBadges(mindfulness_milestone_achieved);
      }
      if (loadHIstory) {
        setFetchHistory(!fetchHistory);
      }
    } catch (error) {
      console.error(
        "Error fetching Data:",
        error.response ? error.response.data : error.message,
      );
      if (isMountedRef.current) {
        toast.error(t("Something went wrong. Redirecting you to try again..."));
        navigate("/mindfulness");
        setFirstLoad(false);
      }
    }
  };

  const handleFailedPlan = () => {
    // toast.error(t("Failed to create plan! , please try again."));
    if (adviseData) {
      setIsPlanLoading(false);
    } else {
      navigate("/mindfulness");
      return;
    }
  };

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, []);

  // Cleanup effect when component unmounts or user navigates away
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getBadgeData = () => {
    // If a badge name is provided, find it in the slider data
    if (lastMilestone) {
      const unlockedBadge = mindfulnessSliderData.find(
        (item) => item.title === lastMilestone?.badge_name,
      );
      if (unlockedBadge) {
        return {
          ...unlockedBadge,
          RankImage: unlockedBadge?.RankImage,
          title: t(`mindfulnessRanks.${unlockedBadge?.title}.title`),
          unlockDate: lastMilestone?.unlocked_at,
          description: t(
            `mindfulnessRanks.${unlockedBadge?.title}.description`,
          ),
          isUnlocked: true,
        };
      }
    }

    // If no badge provided or not found, use the first badge from slider data as locked
    const firstBadge = mindfulnessSliderData[0];
    return {
      RankImage: firstBadge?.RankImage,
      title: t(`mindfulnessRanks.${firstBadge?.title}.title`),
      unlockDate: "Locked",
      description: t(`mindfulnessRanks.${firstBadge?.title}.description`),
      isUnlocked: false,
    };
  };

  const badgeData = getBadgeData();

  if (location.state) {
    return (
      <div>
        {firstLoad ? (
          <ActualDietPlanLoader mindfulness={true} />
        ) : !adviseData && isPlanLoading ? (
          <ReacreationLoading
            timeLeft={timeLeft}
            minuteSet={120}
            data={mindfulnessSlides}
            type="mindfulness"
            loaderHeading={t("mindfulnessLoader.mealPlanProgress")}
            loaderSubheading={t("mindfulnessLoader.analyzingPlan")}
            onComplete={() => fetchData(true)}
            onFailed={handleFailedPlan}
            stopCountdown={() => stopCountdown()}
            setTimeLeft={setTimeLeft}
          />
        ) : (
          <>
            <MainHeader
              heading={t("mindfulnessCoachPreferences.mindfulnessCoachHeading")}
              subheading={t(
                "mindfulnessCoachPreferences.mindfulnessCoachSubHeading",
              )}
              description={t(
                "mindfulnessCoachPreferences.mindfulnessCoachDescription",
              )}
              Image={HeaderImage}
            />
            {isPlanLoading && (
              <div className="mt-6">
                <ReacreationLoading
                  timeLeft={timeLeft}
                  minuteSet={120}
                  data={mindfulnessSlides}
                  type="mindfulness"
                  loaderHeading={t("mindfulnessLoader.mealPlanProgress")}
                  loaderSubheading={t("mindfulnessLoader.analyzingPlan")}
                  onComplete={() => fetchData(true)}
                  onFailed={handleFailedPlan}
                  stopCountdown={() => stopCountdown()}
                  setTimeLeft={setTimeLeft}
                  recreateLoading={true}
                />
              </div>
            )}
            <div className="flex bg-white rounded-full border border-[#E9E9E9] w-[fit-content] gap-0 md:gap-2 mt-7 shadow-md">
              {["Daily Overview", "Weekly Overview"]?.map((item, index) => {
                const isActive = item === tab;
                return (
                  <button
                    key={item}
                    onClick={() => setTab(item)}
                    className={`relative px-6 py-3 text-sm rounded-full capitalize transition-all duration-300  text-center ${
                      isActive ? "text-white" : "text-black bg-white"
                    }`}
                    style={{
                      background: isActive
                        ? "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)"
                        : "transparent",
                    }}
                  >
                    <span className="text-[12px] md:text-[14px] xl:text-[16px] font-[500] font-[Lato]">
                      {t(`mindfulnessCoachPreferences.${item}`)}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-7 gap-5 my-7">
              <div className="lg:col-span-7">
                <DashboardSuggestion
                  adviseData={adviseData}
                  setAdviseData={setAdviseData}
                  setFilterRecord={setFilterRecord}
                  tab={tab}
                  isPlanLoading={isPlanLoading}
                  setBadgeLocked={setLastMilestone}
                  setOpenAchivementModal={setOpenAchivementModal}
                  setUnlockedBadges={setUnlockedBadges}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 xl:grid-cols-7 gap-5 mb-7">
              <div className="xl:col-span-4">
                <Education
                  heading="mindfulnessCoachPreferences.A Calmer Mind, One Practice at a Time"
                  title="mindfulnessCoachPreferences.The Science of Mindfulness"
                  description="mindfulnessCoachPreferences.Small daily practices for a calmer, clearer mind."
                  link="mindfulnessVideoLink"
                />
              </div>

              <div className="xl:col-span-3">
                <div
                  className="p-6 col-span-3 rounded-xl text-white bg-cover xl:h-full relative" // Added relative here
                  style={{ backgroundImage: `url(${stepBack})` }}
                >
                  <div className="flex justify-between items-center mb-10">
                    <h4 className="text-[16px] font-[Lato] text-white">
                      {t("sleepCoachPreferences.Milestones Achieved")}
                    </h4>

                    <button
                      className="bg-white rounded-md text-[#0575e6] text-[12px] font-[Lato] py-2 px-3"
                      onClick={() =>
                        navigate("/mindfulness-ranks", {
                          state: { unlockedBadges , mindfulness : true },
                        })
                      }
                    >
                      {t("View All")}
                    </button>
                  </div>
                  <div className="flex justify-center  w-full mb-10">
                    <div
                      className={`${
                        !lastMilestone && "blur-[8px]"
                      } bg-white rounded-3xl w-[85%] p-5 relative pb-8`}
                    >
                      {" "}
                      <div className="flex justify-center mt-[35%] sm:mt-[18%] lg:mt-[25%] xl:mt-[30%]">
                        <h3
                          className={`${
                            !lastMilestone
                              ? "text-white bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] w-[fit-content] py-2 px-3 my-2"
                              : "text-[#000]"
                          } text-[16px] font-[600] font-[Lato] text-center`}
                        >
                          {badgeData.title}
                        </h3>
                      </div>
                      <div className="flex justify-center">
                        {badgeData.isUnlocked && (
                          <button
                            disabled
                            className="bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] rounded-[22px] py-2 px-4 text-[12px] my-2"
                          >
                            {(() => {
                              const date = new Date(badgeData?.unlockDate);
                              const day = date.getDate();
                              const monthName = date.toLocaleDateString(
                                "en-US",
                                {
                                  month: "long",
                                },
                              );
                              const translatedMonth = t(`month.${monthName}`);

                              return `${t(
                                "quizGame.Unlocked",
                              )} on ${day} ${translatedMonth}`;
                            })()}
                          </button>
                        )}
                      </div>
                      <div className="border border-[#DDDDDD] p-2 rounded-xl">
                        <p className="text-[14px] text-[#5C5C5C] text-center">
                          {badgeData.description}
                        </p>
                      </div>
                      <img
                        src={badgeData.RankImage}
                        alt="image"
                        className={`absolute -top-[8%] left-1/2 -translate-x-1/2 z-[9] w-[40%] sm:w-[25%] md:w-[25%] lg:w-[30%] xl:w-[35%] object-cover" `} // Adjusted positioning and added width
                        style={{ maxHeight: "none" }}
                      />
                    </div>
                  </div>

                  {!lastMilestone && (
                    <img
                      src={LockSleepCoach}
                      alt="image"
                      className="absolute top-[40%] 2xl:top-[30%]  left-1/2 -translate-x-1/2 z-[9] object-cover w-[40px] sm:w-[50px]"
                    />
                  )}
                </div>
              </div>
            </div>
            <DashboardPastPractices
              filterRecord={filterRecord}
              setFilterRecord={setFilterRecord}
              fetchHistory={fetchHistory}
            />
          </>
        )}
        <SleepCoachMilestoneModal
          isOpen={openAchivementModal}
          setIsOpen={setOpenAchivementModal}
          item={badgeData}
          mindfulness={true}
        />
      </div>
    );
  }
};

export default MindfulnessDashboard;
